import { RootState, AppThunk } from "ducks/state";
import { createSelector } from "reselect";
import { newNotification } from "./notification";
import { hen, Hen } from "@udok/lib/internal/store";
import {
  ExamProcedure,
  ExamProcedureFilter,
  Tuss,
  FilterTussList,
  ExamProcedureForm,
} from "@udok/lib/api/models";
import {
  searchProcedures,
  fetchProcedure,
  registerTussUsage,
  fetchAllTuss,
  fetchRecentTuss,
  createProcedures,
  updateProcedures,
  deleteProcedures,
} from "@udok/lib/api/procedure";
import { getToken, UNAUTHORIZED } from "./auth";

export type InitialState = {
  proceduresByID: { [exprID: string]: ExamProcedure | undefined };
  tussByCode: { [tussCode: string]: Tuss | undefined };
  filteredProcedures: string[];
  tussSearch: string[];
  tussRecent: Tuss[];
};

// Reducers
const initialState: InitialState = {
  proceduresByID: {},
  tussByCode: {},
  filteredProcedures: [],
  tussSearch: [],
  tussRecent: [],
};

class Procedures extends Hen<InitialState> {
  listProceduresLoaded(list: ExamProcedure[]) {
    this.state.filteredProcedures = list.map((p) => {
      this.state.proceduresByID[p.exprID] = p;
      return p.exprID;
    });
  }
  procedureLoaded(pr: ExamProcedure) {
    this.state.proceduresByID[pr.exprID] = pr;
  }
  procedureRemoved(pr: ExamProcedure) {
    delete this.state.proceduresByID[pr.exprID];
  }
  loadTussSearch(tuss: Tuss[]) {
    this.state.tussSearch = tuss.map((t) => {
      this.state.tussByCode[t.tussCode] = t;
      return t.tussCode;
    });
  }
  loadTussRecent(tuss: Tuss[]) {
    this.state.tussRecent = tuss;
  }
  tussLoaded(tuss: Tuss) {
    this.state.tussByCode[tuss.tussCode] = tuss;
  }
  clearTussSearch() {
    this.state.tussSearch = [];
  }
}

export const [Reducer, actions] = hen(new Procedures(initialState), {
  [UNAUTHORIZED]: () => {
    return initialState;
  },
});

// Selectors
export const proceduresSelector = (state: RootState) =>
  state.procedure.proceduresByID;
const filteredProceduresSelector = (state: RootState) =>
  state.procedure.filteredProcedures;
const tussSearchRepository = (state: RootState) => state.procedure.tussSearch;
const tussByCodeRepository = (state: RootState) => state.procedure.tussByCode;
const tussRecentRepository = (state: RootState) => state.procedure.tussRecent;

export const searchProceduresListView = createSelector(
  [proceduresSelector, filteredProceduresSelector],
  (proceduresByID, filtered) => {
    const allProcedures = Object.keys(proceduresByID)
      .map((id) => proceduresByID[id])
      .filter((b) => !!b) as ExamProcedure[];
    const filteredProcedures = filtered
      .map((id) => proceduresByID[id])
      .filter((b) => !!b) as ExamProcedure[];
    return {
      allProcedures,
      filteredProcedures,
    };
  }
);

export const getOneProcedureView = (
  state: RootState,
  props: { exprID: string }
) =>
  createSelector([proceduresSelector], (proceduresByID) => {
    return {
      procedure: proceduresByID[props.exprID],
    };
  });

export const getProceduresByID = createSelector(
  [proceduresSelector],
  (proceduresByID) => {
    return {
      proceduresByID,
    };
  }
);

export const getTussSearchView = createSelector(
  [tussSearchRepository, tussByCodeRepository, tussRecentRepository],
  (search, tussByCode, recent) => {
    return {
      listSearch: search
        .map((code) => tussByCode[code])
        .filter((tuss) => !!tuss) as Tuss[],
      listRecent: recent ?? [],
    };
  }
);

export const searchTussListView = createSelector(
  [tussByCodeRepository, tussSearchRepository],
  (tussByCode, filtered) => {
    const allTuss = Object.keys(tussByCode)
      .map((id) => tussByCode[id])
      .filter((b) => !!b) as Tuss[];
    const filteredTuss = filtered
      .map((id) => tussByCode[id])
      .filter((b) => !!b) as Tuss[];
    return {
      allTuss,
      filteredTuss,
    };
  }
);

export const getOneProcedure = (props: { exprID: string }) =>
  createSelector([proceduresSelector], (proceduresByID) => {
    return {
      procedure: proceduresByID[props.exprID],
    };
  });

// Actions
export function searchListProcedures(
  f?: ExamProcedureFilter
): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;
    const fil: ExamProcedureFilter = {
      limit: 100,
      ...f,
    };
    return searchProcedures(fil, apiToken)
      .then((r) => {
        dispatch(actions.listProceduresLoaded(r));
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
      });
  };
}

export function searchScheduleProcedures(
  f?: ExamProcedureFilter
): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const doctID = t.token.payload.doctID;
    const fil: ExamProcedureFilter = {
      limit: 100,
      doctID,
      ...f,
    };
    return searchProcedures(fil)
      .then((r) => {
        dispatch(actions.listProceduresLoaded(r));
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
      });
  };
}

export function loadProcedure(exprID: string): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    return fetchProcedure(exprID)
      .then((r) => {
        dispatch(actions.procedureLoaded(r));
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
      });
  };
}

export function fetchCachedProcedure(exprID: string): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const p = state.procedure.proceduresByID;
    if (Boolean(p[exprID])) {
      return Promise.resolve();
    }

    return dispatch(loadProcedure(exprID));
  };
}

export function searchTuss(filter: FilterTussList): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;
    return fetchAllTuss(apiToken, filter)
      .then((r) => {
        dispatch(actions.loadTussSearch(r));
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
        throw e;
      });
  };
}

export function loadTuss(tussCode: string): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;

    return fetchAllTuss(apiToken, { tussCode })
      .then((r) => {
        dispatch(actions.tussLoaded(r[0]));
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
        throw e;
      });
  };
}

export function fetchCachedTuss(tussCode: string): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const p = state.procedure.tussByCode[tussCode];
    if (Boolean(p)) {
      return Promise.resolve();
    }

    return dispatch(loadTuss(tussCode));
  };
}

export function loadRecentTuss(): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;
    return fetchRecentTuss(apiToken)
      .then((r) => {
        dispatch(actions.loadTussRecent(r));
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
        throw e;
      });
  };
}

export function addTussUsage(tussCode: string): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;
    return registerTussUsage(apiToken, tussCode)
      .then(() => {})
      .catch(() => {});
  };
}

export function clearSearchTuss(): AppThunk<Promise<void>> {
  return async (dispatch) => {
    dispatch(actions.clearTussSearch());
  };
}

export function searchMyProcedures(
  f?: ExamProcedureFilter
): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;
    const createdByDoctID = t.token.payload.doctID;
    const fil: ExamProcedureFilter = {
      createdByDoctID,
      ...f,
    };
    return searchProcedures(fil, apiToken)
      .then((r) => {
        dispatch(actions.listProceduresLoaded(r));
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
        throw e;
      });
  };
}

export function createExamsProcedure(
  data: ExamProcedureForm
): AppThunk<Promise<ExamProcedure>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;
    return createProcedures(apiToken, data)
      .then((r) => {
        dispatch(actions.procedureLoaded(r));
        return r;
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
        throw e;
      });
  };
}

export function updateExamsProcedure(
  exprID: string,
  data: ExamProcedureForm
): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;
    return updateProcedures(apiToken, exprID, data)
      .then((r) => {
        dispatch(actions.procedureLoaded(r));
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
        throw e;
      });
  };
}

export function removeExamsProcedure(exprID: string): AppThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const state = getState();
    const t = getToken(state);
    const apiToken = "Bearer " + t.token.raw;
    return deleteProcedures(apiToken, exprID)
      .then((r) => {
        dispatch(actions.procedureRemoved(r));
      })
      .catch((e) => {
        dispatch(
          newNotification("general", {
            status: "error",
            message: e.message,
          })
        );
        throw e;
      });
  };
}
